<template>
  <generic-modal
    :value="value"
    :is-disabled="isDisabled"
    :is-loading="isLoading"
    title="Create Page"
    persistent
    confirm-text="Create"
    hide-louis
    @modal-confirm="onCreateConfirm"
    @modal-cancel="onCreateCancel"
  >
    <div class="d-flex text-caption mb-8">
      <v-icon class="mr-1" small>info</v-icon>
      <span>This modal is for <b>NEW</b> pages only. Pages will default to English.</span>
    </div>

    <v-form ref="form">
      <autocomplete-input
        v-model="formData.site_id"
        :items="siteOptions"
        :is-loading="isLoading"
        label="Site"
        hide-details
        class="mb-6"
        :rules="rules.site_id"
        required
      />

      <v-card v-if="formData.site_id" class="mb-6 pb-2" outlined>
        <v-card-subtitle class="mb-4 grey lighten-4 pa-3">Details</v-card-subtitle>
        <v-card-text class="py-2">
          <autocomplete-input
            v-model="formData.language_id"
            :items="languageOptions"
            :is-loading="isLoading"
            label="Language"
            hide-details
            class="mb-8"
            :rules="rules.language_id"
            required
          />

          <autocomplete-input
            v-model="formData.page_type_id"
            :items="pageTypeOptions"
            :is-loading="isLoading"
            label="Page Type"
            hide-details
            class="mb-6"
            :rules="rules.page_type_id"
            required
          />
        </v-card-text>
      </v-card>

      <v-card v-if="formData.language_id && formData.page_type_id && entityTypeOptions.length" class="pb-2" outlined>
        <v-card-subtitle class="mb-4 grey lighten-4 pa-3">Attach Entities</v-card-subtitle>
        <v-card-text class="pt-2 pb-0">
          <autocomplete-input
            v-for="(entityType, index) of entityTypeOptions"
            :key="index"
            :items="entityOptions[entityType.value]"
            :is-loading="isLoading"
            :label="entityType.key"
            :value="formData.entityIds[index] && formData.entityIds[index].entity_id"
            hide-details
            class="mb-8"
            :rules="rules.entityId"
            required
            return-object
            @input="onEntityInput($event, index)"
          />
        </v-card-text>
      </v-card>
    </v-form>
  </generic-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { entities, pages } from '@/services/offers'
import GenericModal from '@/components/modals/generic-modal'
import AutocompleteInput from '@/components/forms/autocomplete-input'

export default {
  components: { AutocompleteInput, GenericModal },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      entities: [],
      rules: {
        page_type_id: [v => !!v || 'Page type is required'],
        site_id: [v => !!v || 'Site is required'],
        language_id: [v => !!v || 'Language is required'],
        entityId: [v => !!v || 'Entity is required'],
      },
      formData: {
        page_type_id: null,
        site_id: null,
        language_id: null,
        entityIds: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      pageTypes: 'pageTypes/getPageTypes',
      pageTypeSiteGroups: 'pageTypeSiteGroups/getPageTypeSiteGroups',
      sites: 'sites/getSites',
      languageSites: 'languageSites/getLanguageSites',
      languages: 'languages/getLanguages',
      entityTypePageTypes: 'entityTypePageTypes/getEntityTypePageTypes',
    }),
    isDisabled() {
      if (this.isLoading) return true
      if (!this.formData.page_type_id || !this.formData.site_id || !this.formData.language_id) return true
      if (this.selectedEntities.filter(val => !!val).length !== this.entityTypeOptions.length) return true
      return false
    },
    selectedSite() {
      if (!this.formData.site_id) return {}
      return this.sites.data.find(({ id }) => id == this.formData.site_id)
    },
    selectedLanguage() {
      if (!this.formData.language_id) return {}
      return this.languages.data.find(({ id }) => id == this.formData.language_id)
    },
    selectedPageType() {
      if (!this.formData.page_type_id) return {}
      return this.pageTypes.data.find(({ id }) => id == this.formData.page_type_id)
    },
    selectedEntities() {
      return this.formData.entityIds.map(({ entity_id }) => this.entities.find(({ id }) => id == entity_id))
    },
    siteOptions() {
      return this.sites.data
        .filter(({ id }) => this.languageSites.data.filter(({ site_id }) => site_id === id).length > 0)
        .map(({ id: value, hostname: key }) => ({ value, key }))
    },
    languageOptions() {
      return this.languageSites.data
        .filter(({ site_id }) => site_id === this.formData.site_id)
        .map(({ language: { id: value, name: key } }) => ({ value, key }))
    },
    pageTypeOptions() {
      return this.pageTypeSiteGroups.data
        .filter(({ site_group_id }) => site_group_id === this.selectedSite.site_group_id)
        .map(({ pageType: { id: value, name: key } }) => ({ value, key }))
    },
    entityTypeOptions() {
      return this.entityTypePageTypes.data
        .filter(({ page_type_id }) => page_type_id === this.selectedPageType.id)
        .map(({ label, id: entityTypePageTypeId, entityType: { id: entityTypeId, name } }) => ({
          value: entityTypePageTypeId,
          key: label ? `${label} (${name})` : name,
          entityTypeId,
        }))
    },
    entityOptions() {
      return this.entityTypeOptions.reduce(
        (result, { value, entityTypeId }) =>
          Object.assign(result, {
            [value]: this.entities
              .filter(entity => entity.entity_type_id == entityTypeId)
              .map(({ id, entity: { default: { name } } }) => ({
                key: name,
                value: id,
                entityTypePageTypeId: value,
              })),
          }),
        {}
      )
    },
    transformedPayload() {
      return [
        {
          ...this.formData,
          pageType: this.selectedPageType,
          site: this.selectedSite,
          language: this.selectedLanguage,
          entities: this.selectedEntities,
          entityIds: this.formData.entityIds,
        },
      ]
    },
  },
  watch: {
    'formData.page_type_id': {
      handler() {
        this.formData.entityIds = []
      },
    },
    'formData.site_id': {
      handler() {
        const englishLanguage = this.languageOptions.find(({ key }) => key === 'English')
        if (englishLanguage) return (this.formData.language_id = englishLanguage.value)
        this.formData.language_id = this.languageOptions && this.languageOptions[0] && this.languageOptions[0].value
      },
    },
  },
  async mounted() {
    this.isLoading = true
    const paginate = { perPage: null }
    await this.fetchPageTypes({ paginate })
    await this.fetchPageTypeSiteGroups({ paginate })
    await this.fetchSites({ paginate })
    await this.fetchLanguageSites({ paginate })
    await this.fetchLanguages({ paginate })
    await this.fetchEntityTypePageTypes({ paginate })
    const { data } = await entities.getEntities({})
    this.entities = data
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      fetchPageTypes: 'pageTypes/fetch',
      fetchPageTypeSiteGroups: 'pageTypeSiteGroups/fetch',
      fetchSites: 'sites/fetch',
      fetchLanguageSites: 'languageSites/fetch',
      fetchLanguages: 'languages/fetch',
      fetchEntityTypePageTypes: 'entityTypePageTypes/fetch',
    }),
    async onCreateConfirm() {
      if (!this.$refs.form.validate()) return
      try {
        this.isLoading = true
        const [page] = await pages.bulkCreate(this.transformedPayload)
        if (page.exists) this.$notify({ type: 'warning', text: 'This page already exists' })
        if (!page.exists) this.$notify({ type: 'success', text: 'Successfully created a page' })
        if (!page.id) throw new Error('Unexpected error.')
        this.$router.push({ name: 'content.pages.edit', params: { id: page.id } })
      } catch (error) {
        this.isLoading = false
        this.$notify({ type: 'error', text: 'Unable to create page', data: { error }, duration: -1 })
      }
    },
    onCreateCancel() {
      this.$emit('cancel')
    },
    onEntityInput({ value: entity_id, entityTypePageTypeId }, index) {
      this.$set(
        this.formData,
        'entityIds',
        this.formData.entityIds
          .slice(0, index)
          .concat({ entity_id, entity_type_page_type_id: entityTypePageTypeId })
          .concat(this.formData.entityIds.slice(index))
      )
    },
  },
}
</script>
